import React, { useEffect, useRef, useState } from "react";

export const DEFAULT_FILTERS = {
  account: [],
  meter: [],
  category: [],
  mode: "Interval",
  chip: "1M",
  start: null,
  end: null,
  interval: "Day",
};

const CHIPS = ["D", "W", "1M", "3M", "YTD", "1Y"];

function chipToRange(chip, today = new Date()) {
  const end = new Date(today);
  const start = new Date(today);

  switch (chip) {
    case "D":
      start.setDate(end.getDate() - 1);
      break;
    case "W":
      start.setDate(end.getDate() - 7);
      break;
    case "1M":
      start.setMonth(end.getMonth() - 1);
      break;
    case "3M":
      start.setMonth(end.getMonth() - 3);
      break;
    case "YTD":
      start.setMonth(0, 1);
      break;
    case "1Y":
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      break;
  }

  const fmt = (d) => d.toISOString().slice(0, 10);

  return {
    start: fmt(start),
    end: fmt(end),
  };
}

function MultiSelect({
  label,
  placeholder,
  options = [],
  valueKey,
  labelKey,
  selected = [],
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }

    setOpen(false);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setOpen(false);
  };

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length > 1
      ? `${selected.length} selected`
      : (() => {
          const opt = options.find((o) => o[valueKey] === selected[0]);
          return opt ? opt[labelKey] : selected[0];
        })();

  return (
    <div className="relative" ref={ref}>
      <div className="mb-2 text-xs uppercase tracking-wide text-[#7ea6d9]">
        {label}
      </div>

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex min-h-[44px] w-full items-center justify-between rounded-md border border-[#2a4a73] bg-[#1a3354] px-3 py-2 text-left"
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          {selected.length === 0 ? (
            <span className="block truncate text-sm text-[#d9e8ff]">
              {displayText}
            </span>
          ) : (
            <span className="inline-block max-w-full truncate rounded-full bg-[#0b3a5f] px-3 py-1 text-xs text-[#2ea8ff]">
              {displayText}
            </span>
          )}
        </div>

        <span className="ml-2 text-xs text-[#8ba1c7]">▼</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[9999] mt-1 max-h-[260px] w-full overflow-y-auto rounded-md border border-[#23446f] bg-[#132742] shadow-xl">
          <button
            type="button"
            onClick={clearAll}
            className="block w-full border-b border-[#23446f] px-4 py-2 text-left text-sm text-[#2ea8ff] hover:bg-[#1a3354]"
          >
            × Clear all
          </button>

          {options.map((opt) => {
            const id = opt[valueKey];
            const checked = selected.includes(id);

            return (
              <button
                type="button"
                key={id}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(id);
                }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-white hover:bg-[#1a3354]"
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                    checked
                      ? "border-[#2ea8ff] bg-[#2ea8ff]"
                      : "border-white bg-white"
                  }`}
                >
                  {checked && (
                    <span className="text-[11px] leading-none text-white">
                      ✓
                    </span>
                  )}
                </span>

                <span className="truncate text-sm font-medium">
                  {opt[labelKey]}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function WaterUsageFilters({
  accounts = [],
  meters = [],
  categories = [],
  value = DEFAULT_FILTERS,
  onChange = () => {},
  onDownload = () => {},
}) {
  const f = {
    ...DEFAULT_FILTERS,
    ...value,
  };

  useEffect(() => {
    if (f.chip && (!f.start || !f.end)) {
      const { start, end } = chipToRange(f.chip);

      onChange({
        ...f,
        start,
        end,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => {
    onChange({
      ...f,
      ...patch,
    });
  };

  const onChipClick = (chip) => {
    const { start, end } = chipToRange(chip);

    set({
      chip,
      mode: "Interval",
      start,
      end,
    });
  };

  const onModeClick = (mode) => {
    if (mode === "Custom") {
      set({
        mode,
        chip: null,
      });
    } else {
      set({ mode });
    }
  };

  const onDateInput = (key, val) => {
    set({
      [key]: val,
      mode: "Custom",
      chip: null,
    });
  };

  return (
    <div className="mt-5 overflow-visible rounded-xl border border-[#23446f] bg-[#132742]">
      {/* TOP FILTERS */}
      <div className="grid grid-cols-1 gap-4 border-b border-[#23446f] p-4 md:grid-cols-3">
        <MultiSelect
          label="Accounts"
          placeholder={`All accounts${accounts.length ? ` (${accounts.length})` : ""}`}
          options={accounts}
          valueKey="id"
          labelKey="name"
          selected={f.account || []}
          onChange={(account) => set({ account })}
        />

        <MultiSelect
          label="Meters"
          placeholder={`All meters${meters.length ? ` (${meters.length})` : ""}`}
          options={meters}
          valueKey="id"
          labelKey="name"
          selected={f.meter || []}
          onChange={(meter) => set({ meter })}
        />

        <MultiSelect
          label="Category"
          placeholder="All"
          options={categories}
          valueKey="code"
          labelKey="name"
          selected={f.category || []}
          onChange={(category) => set({ category })}
        />
      </div>

      {/* BOTTOM FILTERS */}
      <div className="flex flex-wrap items-end justify-between gap-5 p-4">
        <div>
          <div className="mb-3 flex items-center gap-2 text-sm">
            {["Range", "Interval", "Custom"].map((mode, index) => (
              <React.Fragment key={mode}>
                <button
                  type="button"
                  onClick={() => onModeClick(mode)}
                  className={`transition-all ${
                    f.mode === mode
                      ? "font-semibold text-[#2ea8ff] text-[12px]"
                      : "text-[#7ea6d9] hover:text-white text-[12px]"
                  }`}
                >
                  {mode}
                </button>

                {index < 2 && <span className="text-[#7ea6d9]">|</span>}
              </React.Fragment>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {CHIPS.map((chip) => (
              <button
                type="button"
                key={chip}
                onClick={() => onChipClick(chip)}
                className={`rounded-md px-4 py-2 text-sm transition-all ${
                  f.chip === chip
                    ? "bg-[#2ea8ff] text-white"
                    : "bg-[#1a3354] text-[#7ea6d9] hover:bg-[#203b60] text-[12px]"
                }`}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={f.start || ""}
              onChange={(e) => onDateInput("start", e.target.value)}
              className="h-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] px-4 text-sm text-[#d9e8ff] outline-none text-[12px]"
            />

            <span className="text-[#7ea6d9]">⇋</span>

            <input
              type="date"
              value={f.end || ""}
              onChange={(e) => onDateInput("end", e.target.value)}
              className="h-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] px-4 text-sm text-[#d9e8ff] outline-none text-[12px]"
            />

            <button
              type="button"
              className="h-[40px] w-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] text-[#8ba1c7] text-[12px]"
            >
              🗓
            </button>
          </div>

          <div>
            <div className="mb-2 text-xs uppercase tracking-wide text-[#7ea6d9]">
              Interval
            </div>

            <div className="flex overflow-hidden rounded-md border border-[#2a4a73] bg-[#1a3354] text-[12px]">
              {["Hour", "Day", "Week"].map((interval) => (
                <button
                  type="button"
                  key={interval}
                  onClick={() => set({ interval })}
                  className={`px-4 py-2 text-sm transition-all ${
                    f.interval === interval
                      ? "bg-[#2ea8ff] text-white"
                      : "text-[#7ea6d9] hover:bg-[#203b60]"
                  }`}
                >
                  {interval}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={onDownload}
            className="h-[40px] w-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] text-[#8ba1c7] hover:text-white"
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}